import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { rentIncreaseCounties, toTitleCase } from '../../data/rentIncreaseNotice';

const RentIncreaseNoticeDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCounties = useMemo(
    () =>
      rentIncreaseCounties.filter((county) =>
        county.includes(searchTerm.toLowerCase().trim())
      ),
    [searchTerm]
  );

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/tools/rent-increase-notice-generator`
      : 'https://rentsmart.ie/tools/rent-increase-notice-generator';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>RPZ & RTB Rent Increase Notice Generator Ireland | RentSmart.ie</title>
        <meta
          name="description"
          content="Generate a county-specific rent increase notice in Ireland. Use RentSmart's RPZ/RTB rent review calculator, get a legal-cap check, and draft a compliant notice in minutes."
        />
        <meta
          name="keywords"
          content="rpz calculator, rent pressure zone calculator, rtb rent calculator, rent increase notice ireland, rent review notice template"
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Rent Increase Notice Generator by County',
            description:
              'County-localized RPZ rent increase compliance pages and notice generator for Ireland.',
            url: canonicalUrl,
          })}
        </script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-12 -right-8 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-8 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <Sparkles className="h-4 w-4" /> Compliance Hub
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            RPZ and RTB Rent Increase Notice Generator for Ireland Landlords
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">
            Build clearer rent review notices with county-level context, legal cap checks, and a
            shareable compliance output that is easier to understand.
          </p>

          <div className="max-w-xl relative mt-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Choose county (e.g. dublin, cork, galway)"
              className="w-full rounded-xl bg-white text-slate-900 pl-12 pr-4 py-4 shadow-xl outline-none focus:ring-4 focus:ring-cyan-200"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10">
        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <FileText className="h-5 w-5 text-sky-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Notice Drafting</h2>
            <p className="text-sm text-slate-600">
              Generate a practical draft with clear rent math and date structure.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <ShieldCheck className="h-5 w-5 text-emerald-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Legal vs Illegal Check</h2>
            <p className="text-sm text-slate-600">
              Instantly compare proposed rent against the calculated maximum.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Sparkles className="h-5 w-5 text-violet-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">County Context</h2>
            <p className="text-sm text-slate-600">
              Unique guidance by county to reduce disputes and improve compliance quality.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-slate-900 mb-4">Choose Your County</h2>
          {filteredCounties.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredCounties.map((countySlug) => (
                <Link
                  key={countySlug}
                  to={`/tools/rent-increase-notice-generator/${countySlug}`}
                  className="no-underline rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    RPZ / RTB Notice Tool
                  </p>
                  <p className="text-xl font-black text-slate-900 mb-1">
                    {toTitleCase(countySlug)}
                  </p>
                  <p className="text-sm font-semibold text-slate-600 mb-4">
                    Rent increase compliance generator
                  </p>
                  <p className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-700">
                    Open tool <ArrowRight className="h-3 w-3" />
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-100 p-5">
          <p className="text-xs text-slate-600 font-semibold leading-relaxed">
            This tool is informational only and does not replace official RTB guidance or legal advice.
            Always validate outcomes against current regulation and professional advice.
          </p>
        </section>
      </main>
    </div>
  );
};

export default RentIncreaseNoticeDiscoverPage;
